const AI_SESSION_KEY = 'logiflow.ai.sessions';
const AI_LAST_RESULT_KEY_PREFIX = 'logiflow.ai.lastResult.';
const CLIENT_LISTING_PREFILL_KEY = 'logiflow.ai.clientListingPrefill';
const CLIENT_LISTING_EDIT_PREFILL_KEY = 'logiflow.ai.clientListingEditPrefill';
const CLIENT_REVIEW_DRAFT_KEY = 'logiflow.ai.clientReviewDraft';
const MESSAGE_DRAFT_KEY = 'logiflow.ai.messageDraft';
const PARTNER_MARKETPLACE_FILTERS_KEY = 'logiflow.ai.partnerMarketplaceFilters';
const AI_PREFERENCES_KEY = 'logiflow.ai.preferences';

const AI_PREFERENCE_DEFAULTS = {
  showLaunchers: true,
  rememberSession: false,
  autoOpenPreparedScreens: true,
};

const getWebStorage = (kind = 'session') => {
  if (typeof window === 'undefined') {
    return null;
  }

  return kind === 'local' ? window.localStorage : window.sessionStorage;
};

const readJson = (key, fallback) => {
  const storage = getWebStorage('session');
  if (!storage) {
    return fallback;
  }

  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const readJsonFrom = (kind, key, fallback) => {
  const storage = getWebStorage(kind);
  if (!storage) {
    return fallback;
  }

  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value, kind = 'session') => {
  const storage = getWebStorage(kind);
  if (!storage) {
    return;
  }

  storage.setItem(key, JSON.stringify(value));
};

const removeItem = (key, kind = 'session') => {
  const storage = getWebStorage(kind);
  if (!storage) {
    return;
  }

  storage.removeItem(key);
};

export const getAiPreferences = () => ({
  ...AI_PREFERENCE_DEFAULTS,
  ...readJsonFrom('local', AI_PREFERENCES_KEY, {}),
});

export const setAiPreferences = (preferences = {}) => {
  const nextPreferences = {
    ...getAiPreferences(),
    ...preferences,
  };

  writeJson(AI_PREFERENCES_KEY, nextPreferences, 'local');

  if (!nextPreferences.rememberSession) {
    removeItem(AI_SESSION_KEY, 'local');
  }

  return nextPreferences;
};

const getSessionStorageKind = () => (getAiPreferences().rememberSession ? 'local' : 'session');

export const clearAiSessionIds = () => {
  removeItem(AI_SESSION_KEY, 'session');
  removeItem(AI_SESSION_KEY, 'local');
};

export const clearAiSessionId = (role) => {
  ['session', 'local'].forEach((kind) => {
    const sessions = readJsonFrom(kind, AI_SESSION_KEY, {});
    if (!sessions?.[role]) {
      return;
    }

    const nextSessions = { ...sessions };
    delete nextSessions[role];

    if (Object.keys(nextSessions).length === 0) {
      removeItem(AI_SESSION_KEY, kind);
      return;
    }

    writeJson(AI_SESSION_KEY, nextSessions, kind);
  });
};

export const getAiSessionId = (role) => {
  const preferredKind = getSessionStorageKind();
  const fallbackKind = preferredKind === 'local' ? 'session' : 'local';
  const sessions =
    readJsonFrom(preferredKind, AI_SESSION_KEY, null) ||
    readJsonFrom(fallbackKind, AI_SESSION_KEY, {});
  return typeof sessions?.[role] === 'string' ? sessions[role] : null;
};

export const setAiSessionId = (role, sessionId) => {
  const storageKind = getSessionStorageKind();
  const sessions = readJsonFrom(storageKind, AI_SESSION_KEY, {});
  writeJson(AI_SESSION_KEY, {
    ...sessions,
    [role]: sessionId,
  }, storageKind);

  removeItem(AI_SESSION_KEY, storageKind === 'local' ? 'session' : 'local');
};

export const setClientListingPrefill = (prefill) => {
  writeJson(CLIENT_LISTING_PREFILL_KEY, prefill || null);
};

export const consumeClientListingPrefill = () => {
  const value = readJson(CLIENT_LISTING_PREFILL_KEY, null);
  removeItem(CLIENT_LISTING_PREFILL_KEY);
  return value;
};

export const setClientListingEditPrefill = (listingId, prefill) => {
  writeJson(CLIENT_LISTING_EDIT_PREFILL_KEY, {
    listingId,
    prefill: prefill || {},
  });
};

export const consumeClientListingEditPrefill = (listingId) => {
  const value = readJson(CLIENT_LISTING_EDIT_PREFILL_KEY, null);
  removeItem(CLIENT_LISTING_EDIT_PREFILL_KEY);

  if (!value || String(value.listingId || '') !== String(listingId || '')) {
    return null;
  }

  return value.prefill || {};
};

export const setClientReviewDraft = (draft) => {
  writeJson(CLIENT_REVIEW_DRAFT_KEY, draft || null);
};

export const consumeClientReviewDraft = (listingId) => {
  const value = readJson(CLIENT_REVIEW_DRAFT_KEY, null);
  removeItem(CLIENT_REVIEW_DRAFT_KEY);

  if (!value || String(value.listingId || '') !== String(listingId || '')) {
    return null;
  }

  return value;
};

export const setPartnerMarketplaceFilters = (filters) => {
  writeJson(PARTNER_MARKETPLACE_FILTERS_KEY, filters || null);
};

export const consumePartnerMarketplaceFilters = () => {
  const value = readJson(PARTNER_MARKETPLACE_FILTERS_KEY, null);
  removeItem(PARTNER_MARKETPLACE_FILTERS_KEY);
  return value;
};

export const getAiLastResult = (role) =>
  readJsonFrom('local', `${AI_LAST_RESULT_KEY_PREFIX}${role}`, null);

export const setAiLastResult = (role, result) =>
  writeJson(`${AI_LAST_RESULT_KEY_PREFIX}${role}`, result || null, 'local');

export const clearAiLastResult = (role) =>
  removeItem(`${AI_LAST_RESULT_KEY_PREFIX}${role}`, 'local');

export const setMessageDraft = (draft) => {
  writeJson(MESSAGE_DRAFT_KEY, draft || null);
};

export const consumeMessageDraft = (role, listingId) => {
  const value = readJson(MESSAGE_DRAFT_KEY, null);
  removeItem(MESSAGE_DRAFT_KEY);

  if (
    !value ||
    String(value.role || '') !== String(role || '') ||
    String(value.listingId || '') !== String(listingId || '')
  ) {
    return null;
  }

  return typeof value.draft === 'string' ? value.draft : '';
};
